import { Divider, Dropdown, Space } from 'antd';
import moment from 'moment';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectFinancialsGroupBy } from 'api/project';
import { ReactComponent as Result } from 'assets/svgs/result.svg';
import { Button } from 'components/atoms/button';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { Menu } from 'components/atoms/dropdown';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { formatterWithMinutes } from 'components/atoms/input-duration/formatter';
import { Switcher } from 'components/atoms/switcher';
import { FilterBar } from 'components/molecules/filter-bar';
import {
  BodyShort01,
  Caption01,
  HelperText01,
  Label01,
  ProductiveHeading03,
  ProductiveHeading06,
  ProductiveHeading07,
} from 'components/styles';
import {
  ProjectFinancialsChartLegendProps,
  ProjectFinancialsChartTooltipProps,
  ProjectFinancialsGroupByParams,
  ProjectFinancialsTableNameCellRendererParams,
  ProjectFinancialsZoomLevelSwitcherParams,
} from '../types';
import { percentageRenderer } from '../utils/common';

export const Result16 = styled(Result)`
  height: 16px;
  width: 16px;
`;

export const HiddenInfo = styled.div`
  ${Caption01};
  color: var(--gray-70);
  text-decoration: underline;
  visibility: hidden;
`;

export const FinancialsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const FilterBarWithExtraPadding = styled(FilterBar)`
  padding: var(--spacing-02) var(--spacing-06);
`;

export const FinancialsContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--spacing-05);
  background: var(--coolest-gray-10);
  padding: var(--layout-02) var(--layout-02);
  overflow: auto;
  height: 100%;
`;

export const RowWrapper = styled.section<{ reverse?: boolean }>`
  display: flex;
  gap: var(--spacing-05);
  width: 100%;
  flex-direction: ${(props) => (props.reverse ? 'row-reverse' : 'row')};
`;

export const VerticalRowWrapper = styled.section<{ reverse?: boolean }>`
  display: flex;
  gap: var(--spacing-05);
  flex-direction: ${(props) => (props.reverse ? 'column-reverse' : 'column')};
`;

export const NoDataWrapper = styled.div`
  padding: var(--spacing-04) 0;
  flex: 1;
`;

export const DataCardTitleWrapper = styled.div`
  ${ProductiveHeading07};
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

export const StatsRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: flex-start;
`;

export const StatCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 80px;
  gap: var(--spacing-02);
`;

export const StatLabelWrapper = styled.div`
  ${HelperText01};
  color: var(--gray-80);
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

export const StatValueWrapper = styled.div`
  ${ProductiveHeading06};
  font-weight: var(--font-weight-normal);
  color: var(--gray-100);
`;

export const HeaderCellWrapper = styled.div`
  ${ProductiveHeading07};
`;

export const NameCellWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: var(--spacing-03);
`;

const NameCellPrefix = styled.div`
  display: flex;
`;

const NameCellContent = styled.div`
  display: flex;
  flex: 1;
`;

const NameCellSuffix = styled.div`
  display: flex;
  align-items: center;
  color: var(--gray-50);
`;

export const ResourceNameCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${BodyShort01};
`;

export const PlaceholderDataWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  ${Caption01};
`;

export const StyledDivider = styled(Divider)`
  margin: 0;
  align-self: center;

  &.ant-divider-vertical {
    height: calc(100% - (2 * var(--spacing-05)));
  }
`;

export const ChartContainer = styled.div`
  align-self: center;
  width: 100%;
  height: 100%;

  .highcharts-scrollbar {
    height: 0;
    opacity: 0;
    transition: opacity 0.5s;
  }

  &:hover .highcharts-scrollbar {
    opacity: 1;
  }
`;

export const ChartLegendIndicator = styled.span`
  display: block;
  width: var(--spacing-03);
  background-color: ${({ color, shape }: ProjectFinancialsChartLegendProps) =>
    shape === 'circle' ? color : 'transparent'};
  height: ${(props: ProjectFinancialsChartLegendProps) =>
    props.shape === 'circle' ? 'var(--spacing-03)' : '0px'};
  border: ${({ shape, color }: ProjectFinancialsChartLegendProps) =>
    shape === 'circle'
      ? 'none'
      : `1px ${shape === 'dashed' ? 'dashed' : 'solid'} ${color}`};
  border-radius: ${(props: ProjectFinancialsChartLegendProps) =>
    props.shape === 'circle' ? '50%' : '2px'};
`;

export const GaugeChartContainer = styled(ChartContainer)`
  max-width: 390px;
`;

export const GaugeChartLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-02);
`;

export const GaugeChartValue = styled.span`
  ${ProductiveHeading03};
  color: var(--gray-100);
`;

export const GaugeChartLabel = styled.span`
  ${Caption01};
  color: var(--gray-100);
`;

export const ChartTooltipContainer = styled.div`
  display: flex;
  padding: var(--spacing-03);
  flex-direction: column;
  gap: var(--spacing-02);
  min-width: 240px;
  background-color: var(--white-100);
  box-shadow: 0px 3.2px 7.2px rgba(0, 0, 0, 0.13),
    0px 0.6px 1.8px rgba(0, 0, 0, 0.11);
`;

export const ChartTooltipTitleContainer = styled.div`
  ${Label01};
  color: var(--gray-60);
  padding-bottom: var(--spacing-03);
`;

export const ChartTooltipItemContinter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-03);
`;

export const ChartTooltipItemLegendContinter = styled.div``;

export const ChartTooltipItemLabelContinter = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  flex-grow: 1;
  color: var(--gray-100);
`;

export const ChartTooltipItemValueContinter = styled.div`
  justify-self: flex-end;
  font-weight: var(--font-weight-bold);
`;

export const ChartTooltipRenderer: FC<ProjectFinancialsChartTooltipProps> = ({
  x,
  points,
  currencyCode,
  revenueLabels,
  timeLabels,
  percentageLabels,
  maxValue,
  maxLabel,
  remainingValue,
  remainingLabel,
}) => {
  // Tried using context here, but seems like the values are not defined when used inside highcharts options
  // hence reading values from props
  const valueRenderer = useCallback(
    (name: string, value: number, type: string) => {
      if (revenueLabels.includes(name)) {
        return currencyRenderer(value, currencyCode);
      }

      if (timeLabels.includes(name)) {
        return formatterWithMinutes(type === 'solidgauge' ? value : value * 60);
      }

      if (percentageLabels.includes(name)) {
        return percentageRenderer(value);
      }

      return value;
    },
    [currencyCode, percentageLabels, revenueLabels, timeLabels],
  );

  return (
    <ChartTooltipContainer>
      {x && (
        <ChartTooltipTitleContainer>
          <HumanisedDate timestamp={moment(x)} />
        </ChartTooltipTitleContainer>
      )}
      {points?.map(
        ({
          series: {
            name,
            userOptions: { type },
          },
          y,
          color,
        }) => (
          <>
            {type === 'solidgauge' && maxValue != null && maxLabel != null && (
              <ChartTooltipItemContinter>
                <ChartTooltipItemLabelContinter>
                  <ChartLegendIndicator color="var(--gray-70)" shape="circle" />
                  {maxLabel}:
                </ChartTooltipItemLabelContinter>
                <ChartTooltipItemValueContinter>
                  {valueRenderer(maxLabel, maxValue, type)}
                </ChartTooltipItemValueContinter>
              </ChartTooltipItemContinter>
            )}
            <ChartTooltipItemContinter>
              <ChartTooltipItemLabelContinter>
                <ChartLegendIndicator
                  color={color as string}
                  shape={
                    type === 'spline'
                      ? name.toLowerCase().includes('estimated')
                        ? 'dashed'
                        : 'line'
                      : 'circle'
                  }
                />
                {name}:
              </ChartTooltipItemLabelContinter>
              <ChartTooltipItemValueContinter>
                {valueRenderer(name, y, type)}
              </ChartTooltipItemValueContinter>
            </ChartTooltipItemContinter>
            {type === 'solidgauge' &&
              remainingValue != null &&
              remainingLabel != null && (
                <ChartTooltipItemContinter>
                  <ChartTooltipItemLabelContinter>
                    <ChartLegendIndicator
                      color="var(--gray-70)"
                      shape="circle"
                    />
                    {remainingLabel}:
                  </ChartTooltipItemLabelContinter>
                  <ChartTooltipItemValueContinter>
                    {valueRenderer(remainingLabel, remainingValue, type)}
                  </ChartTooltipItemValueContinter>
                </ChartTooltipItemContinter>
              )}
          </>
        ),
      )}
    </ChartTooltipContainer>
  );
};

export const ProjectFinancialsGroupByRenderer: FC<ProjectFinancialsGroupByParams> =
  ({ options, selectedOption, onGroupByChange }) => {
    const handleOnClick = useCallback(
      (_params) => {
        onGroupByChange(_params.key);
      },
      [onGroupByChange],
    );

    const GroupByTrigger: FC<{
      selectedGroupBy: ProjectFinancialsGroupBy;
    }> = ({ selectedGroupBy }) => {
      return (
        <Space size={4}>
          <FormattedMessage id="group_by" />:
          <strong>
            <FormattedMessage
              id={`project.financials.group_by.${selectedGroupBy}`}
            />
          </strong>
        </Space>
      );
    };

    const menu = () => (
      <Menu selectedKeys={[selectedOption]} multiple={false}>
        {options.map((option) => (
          <Menu.Item key={option} onClick={handleOnClick}>
            <FormattedMessage id={`project.financials.group_by.${option}`} />
          </Menu.Item>
        ))}
      </Menu>
    );

    return (
      <Dropdown overlay={menu} trigger={['hover']}>
        <Button type="text" size="small">
          <GroupByTrigger selectedGroupBy={selectedOption} />
        </Button>
      </Dropdown>
    );
  };

export const ProjectFinancialsZoomLevelSwitcher: FC<ProjectFinancialsZoomLevelSwitcherParams> =
  ({ activeTab, tabs, onTabSwitch }) => {
    return tabs.length > 1 ? (
      <Switcher
        size="small"
        value={activeTab}
        onChange={(e) => onTabSwitch(e.target.value)}
        activeColor="black"
      >
        {tabs.map((tab) => (
          <Switcher.Button key={tab} value={tab}>
            <FormattedMessage id={`project.financials.zoom_level.${tab}`} />
          </Switcher.Button>
        ))}
      </Switcher>
    ) : null;
  };

export const ProjectFinancialsTableNameCellRenderer: FC<ProjectFinancialsTableNameCellRendererParams> =
  ({ prefix, suffix, children }) => {
    return (
      <NameCellWrapper>
        {prefix != null && <NameCellPrefix>{prefix}</NameCellPrefix>}
        <NameCellContent>{children}</NameCellContent>
        {suffix != null && <NameCellSuffix>{suffix}</NameCellSuffix>}
      </NameCellWrapper>
    );
  };
